import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass/styled-components';
import { CircleCheckBig } from 'lucide-react';
import { Checkbox, ProfilePicture } from '@getro/rombo';
import { ShortTextField, LongTextField, EmailField, UrlField, SelectField, CheckboxField } from './fieldRenderers';

export const fieldRendererMap = {
  text: ShortTextField,
  long_text: LongTextField,
  email: EmailField,
  url: UrlField,
  select: SelectField,
  checkbox: CheckboxField,
};

export const SignupFormPreview = ({
  formState,
  networkName = '[Network Name]',
  type = 'form',
  networkLogo,
  values,
  errors,
  onChange,
  onBlur,
  onConsentChange,
  consentValues,
  consentErrors,
  disabled,
  isPreviewOnly,
}) => {
  if (!formState) return null;

  if (type === 'success') {
    return (
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '24px',
          alignItems: 'flex-start',
          textAlign: 'left',
          pt: '40px',
          px: '24px',
          maxWidth: '520px',
          margin: '0 auto',
        }}
      >
        <Box as={CircleCheckBig} size={48} sx={{ color: 'icon.success' }} />
        <Flex sx={{ flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
          <Text
            sx={{
              fontSize: '16px',
              fontWeight: '600',
              lineHeight: '1.4',
              textAlign: 'left',
            }}
          >
            {formState.successMessageTitle || 'Thanks for joining!'}
          </Text>
          <Text
            sx={{
              fontSize: '14px',
              color: 'text.main',
              lineHeight: '1.4',
              textAlign: 'left',
            }}
          >
            {formState.successMessageDescription || "We'll get in touch when there are relevant opportunities."}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        gap: '48px',
        width: '100%',
        maxWidth: '520px',
        margin: '0 auto',
        py: '32px',
      }}
    >
      <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
        <Flex
          sx={{
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <Text
            sx={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'text.dark',
              lineHeight: '24px',
            }}
          >
            {formState.title || 'Form Title'}
          </Text>
          <Text
            sx={{
              fontSize: '16px',
              color: 'text.main',
              lineHeight: '24px',
            }}
          >
            {formState.description || 'Form description'}
          </Text>
        </Flex>
        <Flex sx={{ alignItems: 'center', gap: '8px' }}>
          <ProfilePicture
            imageUrl={networkLogo}
            name={networkName}
            variant="square"
            width="48px"
            height="48px"
            sx={{ flexShrink: 0 }}
          />
          <Text
            sx={{
              fontSize: '14px',
              color: 'text.main',
            }}
          >
            <Text as="span">A talent community by </Text>
            <Text as="span" sx={{ fontWeight: '500' }}>
              {networkName}
            </Text>
          </Text>
        </Flex>
      </Flex>

      <Flex sx={{ flexDirection: 'column', gap: '24px' }}>
        {formState.fields.length === 0 ? (
          <Box
            sx={{
              p: '32px',
              textAlign: 'center',
              border: '1px dashed',
              borderColor: 'border.subtle',
              borderRadius: '4px',
              color: 'text.subtle',
            }}
          >
            No fields added yet. Switch to Edit mode to add fields.
          </Box>
        ) : (
          <>
            {formState.fields.map((field) => {
              const FieldRenderer = fieldRendererMap[field.type];
              const fieldId = field.id?.toString() || field.tempId;

              if (!fieldId) {
                return null;
              }

              return FieldRenderer ? (
                <FieldRenderer
                  key={fieldId}
                  field={field}
                  value={values?.[fieldId]}
                  onChange={onChange ? (e) => onChange(fieldId, e.target.value) : undefined}
                  onBlur={onBlur ? (e) => onBlur(fieldId, e?.target?.value) : undefined}
                  error={errors?.[fieldId]}
                  disabled={disabled}
                />
              ) : (
                <Box key={fieldId} sx={{ color: 'text.subtle' }}>
                  Unknown field type: {field.type}
                </Box>
              );
            })}

            <Flex sx={{ gap: '8px', alignItems: 'flex-start' }}>
              <Box sx={{ flexShrink: 0, mt: '2px' }}>
                <Checkbox
                  checked={consentValues?.termsAccepted || false}
                  onChange={onConsentChange ? (e) => onConsentChange('termsAccepted', e.target.checked) : undefined}
                  disabled={disabled || isPreviewOnly}
                />
              </Box>
              <Flex sx={{ flexDirection: 'column', gap: '4px' }}>
                <Text
                  sx={{
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: 'text.main',
                  }}
                >
                  I have read and I do accept the{' '}
                  <Text
                    as="a"
                    href="https://www.getro.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'text.main',
                      textDecoration: 'underline',
                      fontWeight: '500',
                    }}
                  >
                    terms of use
                  </Text>{' '}
                  and{' '}
                  <Text
                    as="a"
                    href="https://www.getro.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'text.main',
                      textDecoration: 'underline',
                      fontWeight: '500',
                    }}
                  >
                    privacy policy
                  </Text>
                  .
                </Text>
                {consentErrors?.termsAccepted && (
                  <Text sx={{ fontSize: '12px', color: 'danger' }}>{consentErrors.termsAccepted}</Text>
                )}
              </Flex>
            </Flex>

            <Box
              as="button"
              type="submit"
              sx={{
                width: '100%',
                height: '40px',
                bg: 'purple.400',
                borderRadius: '4px',
                border: 'none',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                lineHeight: '20px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              disabled={disabled}
            >
              {formState.submitButtonText || 'Join now'}
            </Box>
          </>
        )}
      </Flex>
    </Flex>
  );
};

SignupFormPreview.propTypes = {
  formState: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    fields: PropTypes.array,
    submitButtonText: PropTypes.string,
    successMessageTitle: PropTypes.string,
    successMessageDescription: PropTypes.string,
  }),
  networkName: PropTypes.string,
  networkLogo: PropTypes.string,
  type: PropTypes.oneOf(['form', 'success']),
  values: PropTypes.object,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onConsentChange: PropTypes.func,
  consentValues: PropTypes.shape({
    marketingConsent: PropTypes.bool,
    termsAccepted: PropTypes.bool,
  }),
  consentErrors: PropTypes.shape({
    marketingConsent: PropTypes.string,
    termsAccepted: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  isPreviewOnly: PropTypes.bool,
};

SignupFormPreview.defaultProps = {
  formState: null,
  networkName: '[Network Name]',
  networkLogo: null,
  type: 'form',
  values: {},
  errors: {},
  onChange: undefined,
  onBlur: undefined,
  onConsentChange: undefined,
  consentValues: {},
  consentErrors: {},
  disabled: false,
  isPreviewOnly: false,
};
